import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import { 
  CreditCard, Building2, Wallet, Loader2, CheckCircle, ArrowRight, 
  ArrowDownToLine, ArrowUpFromLine, ExternalLink, RefreshCw 
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENCIES = [
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', country: 'nigeria' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵', country: 'ghana' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', country: 'kenya' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', country: 'south_africa' },
];

export default function PaymentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('NGN');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  
  // Deposit form
  const [depositAmount, setDepositAmount] = useState('');
  
  // Withdrawal form
  const [withdrawForm, setWithdrawForm] = useState({
    amount: '',
    bank_code: '',
    account_number: '',
    account_name: ''
  });

  const token = localStorage.getItem('onepay_token');
  const currency = CURRENCIES.find(c => c.code === selectedCurrency);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Check for payment callback
    const reference = searchParams.get('reference');
    if (reference) {
      verifyPayment(reference);
    }
    
    fetchBanks(currency?.country || 'nigeria');
  }, [token, navigate, selectedCurrency]);

  const fetchBanks = async (country) => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/paystack/banks/${country}`);
      setBanks(response.data.banks || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const verifyPayment = async (reference) => {
    setVerifyingPayment(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/payments/paystack/verify/${reference}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.status === 'success') {
        toast({
          title: 'Payment Successful!',
          description: `${currency?.symbol}${response.data.amount} has been added to your wallet`
        });
        // Clear the URL params
        navigate('/payments', { replace: true });
      } else {
        toast({
          title: 'Payment Failed',
          description: response.data.error || 'Unable to verify payment',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Verification Error',
        description: 'Unable to verify payment status',
        variant: 'destructive'
      });
    } finally {
      setVerifyingPayment(false);
    }
  };

  const initiateDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) < 100) {
      toast({ title: 'Error', description: 'Minimum deposit is 100', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const callbackUrl = `${window.location.origin}/payments`;
      
      const response = await axios.post(`${API_URL}/api/payments/paystack/initialize`, {
        amount: parseFloat(depositAmount),
        currency: selectedCurrency,
        callback_url: callbackUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Redirect to Paystack checkout
        if (response.data.authorization_url) {
          window.location.href = response.data.authorization_url;
        } else {
          // Mock mode - simulate success
          toast({
            title: 'Demo Mode',
            description: `In production, you would be redirected to Paystack. Reference: ${response.data.reference}`
          });
        }
      } else {
        toast({
          title: 'Error',
          description: response.data.error || 'Failed to initiate payment',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to initiate deposit',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const resolveAccount = async () => {
    if (!withdrawForm.account_number || !withdrawForm.bank_code) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/payments/paystack/resolve?account_number=${withdrawForm.account_number}&bank_code=${withdrawForm.bank_code}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setWithdrawForm({
          ...withdrawForm,
          account_name: response.data.account_name
        });
      }
    } catch (error) {
      // Account resolution may fail in sandbox
      setWithdrawForm({
        ...withdrawForm,
        account_name: 'Account Holder'
      });
    } finally {
      setLoading(false);
    }
  };

  const initiateWithdrawal = () => {
    if (!withdrawForm.amount || !withdrawForm.bank_code || !withdrawForm.account_number) {
      toast({ title: 'Error', description: 'Please fill all fields', variant: 'destructive' });
      return;
    }
    
    setPendingAction({
      type: 'withdrawal',
      amount: withdrawForm.amount,
      bank: banks.find(b => b.code === withdrawForm.bank_code)?.name,
      account: withdrawForm.account_number,
      account_name: withdrawForm.account_name
    });
    setShowConfirmDialog(true);
  };

  const executeWithdrawal = async () => {
    setShowConfirmDialog(false);
    setLoading(true);
    
    try {
      // First create recipient
      const recipientResponse = await axios.post(`${API_URL}/api/payments/paystack/recipient`, {
        name: withdrawForm.account_name || 'Account Holder',
        account_number: withdrawForm.account_number,
        bank_code: withdrawForm.bank_code,
        currency: selectedCurrency
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!recipientResponse.data.success) {
        throw new Error(recipientResponse.data.error);
      }

      // Then initiate transfer
      const transferResponse = await axios.post(`${API_URL}/api/payments/paystack/transfer`, {
        amount: parseFloat(withdrawForm.amount),
        recipient_code: recipientResponse.data.recipient_code,
        currency: selectedCurrency,
        reason: 'OnePay Withdrawal'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (transferResponse.data.success) {
        toast({
          title: 'Withdrawal Initiated!',
          description: `${currency?.symbol}${withdrawForm.amount} will be sent to your account`
        });
        setWithdrawForm({ amount: '', bank_code: '', account_number: '', account_name: '' });
      } else {
        toast({
          title: 'Error',
          description: transferResponse.data.error,
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.message || 'Withdrawal failed',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="payments-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Payments</h1>
            <p className="text-muted-foreground">Deposit & withdraw funds via Paystack</p>
          </div>
          
          {/* Currency Selector */}
          <div className="w-full md:w-48">
            <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
              <SelectTrigger data-testid="currency-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(c => (
                  <SelectItem key={c.code} value={c.code}>
                    <span className="flex items-center gap-2">
                      <span>{c.symbol}</span>
                      <span>{c.code}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Verifying Payment Overlay */}
        {verifyingPayment && (
          <Card className="border-teal-500 bg-teal-500/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-teal-500" />
                <span>Verifying your payment...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs defaultValue="deposit" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:w-96">
            <TabsTrigger value="deposit" className="flex items-center gap-2">
              <ArrowDownToLine className="w-4 h-4" />
              Deposit
            </TabsTrigger>
            <TabsTrigger value="withdraw" className="flex items-center gap-2">
              <ArrowUpFromLine className="w-4 h-4" />
              Withdraw
            </TabsTrigger>
          </TabsList>

          {/* Deposit Tab */}
          <TabsContent value="deposit">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-teal-500" />
                    Add Money
                  </CardTitle>
                  <CardDescription>
                    Fund your wallet via card, bank transfer, or USSD
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Amount ({selectedCurrency})</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {currency?.symbol}
                      </span>
                      <Input
                        data-testid="deposit-amount-input"
                        type="number"
                        className="pl-10"
                        placeholder="5,000"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Minimum: {currency?.symbol}100</p>
                  </div>

                  {/* Quick amounts */}
                  <div className="flex flex-wrap gap-2">
                    {[1000, 5000, 10000, 25000, 50000].map(amt => (
                      <Button
                        key={amt}
                        variant="outline"
                        size="sm"
                        onClick={() => setDepositAmount(amt.toString())}
                      >
                        {currency?.symbol}{amt.toLocaleString()}
                      </Button>
                    ))}
                  </div>

                  <Button 
                    data-testid="deposit-btn"
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={initiateDeposit}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Wallet className="w-4 h-4 mr-2" />
                    )}
                    Continue to Payment
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              {/* Payment Methods Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Payment Methods</CardTitle>
                  <CardDescription>Secure payments powered by Paystack</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { icon: CreditCard, title: 'Card Payment', desc: 'Visa, Mastercard, Verve' },
                    { icon: Building2, title: 'Bank Transfer', desc: 'Direct bank transfer' },
                    { icon: Wallet, title: 'USSD', desc: 'Pay with USSD code' },
                  ].map((method, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center">
                        <method.icon className="w-5 h-5 text-teal-500" />
                      </div>
                      <div>
                        <p className="font-medium">{method.title}</p>
                        <p className="text-xs text-muted-foreground">{method.desc}</p>
                      </div>
                    </div>
                  ))}
                  
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-sm font-medium text-teal-600 dark:text-teal-400">Paystack</p>
                    <p className="text-xs text-muted-foreground">Secured payments</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Withdraw Tab */}
          <TabsContent value="withdraw">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-teal-500" />
                    Withdraw to Bank
                  </CardTitle>
                  <CardDescription>
                    Transfer funds to your bank account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select Bank</Label>
                    <Select 
                      value={withdrawForm.bank_code}
                      onValueChange={(v) => setWithdrawForm({...withdrawForm, bank_code: v, account_name: ''})}
                    >
                      <SelectTrigger data-testid="bank-selector">
                        <SelectValue placeholder="Choose your bank" />
                      </SelectTrigger>
                      <SelectContent>
                        {banks.map(bank => (
                          <SelectItem key={bank.code} value={bank.code}>{bank.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Account Number</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="account-number-input"
                        placeholder="0123456789"
                        value={withdrawForm.account_number}
                        onChange={(e) => setWithdrawForm({...withdrawForm, account_number: e.target.value})}
                        maxLength={10}
                      />
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={resolveAccount}
                        disabled={loading || !withdrawForm.bank_code || !withdrawForm.account_number}
                      >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>

                  {withdrawForm.account_name && (
                    <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-3">
                      <p className="text-sm text-muted-foreground">Account Name:</p>
                      <p className="font-medium text-teal-700 dark:text-teal-300">
                        {withdrawForm.account_name}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Amount ({selectedCurrency})</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {currency?.symbol}
                      </span>
                      <Input
                        data-testid="withdraw-amount-input"
                        type="number"
                        className="pl-10"
                        placeholder="5,000"
                        value={withdrawForm.amount}
                        onChange={(e) => setWithdrawForm({...withdrawForm, amount: e.target.value})}
                      />
                    </div>
                  </div>

                  <Button 
                    data-testid="withdraw-btn"
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={initiateWithdrawal}
                    disabled={loading || !withdrawForm.account_name}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ArrowUpFromLine className="w-4 h-4 mr-2" />
                    )}
                    Withdraw Funds
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              {/* Withdrawal Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Withdrawal Info</CardTitle>
                  <CardDescription>Important information about withdrawals</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5" />
                      <div>
                        <p className="font-medium">Instant Processing</p>
                        <p className="text-sm text-muted-foreground">Most withdrawals arrive within minutes</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5" />
                      <div>
                        <p className="font-medium">No Hidden Fees</p>
                        <p className="text-sm text-muted-foreground">Standard bank transfer fees apply</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5" />
                      <div>
                        <p className="font-medium">Secure Transfer</p>
                        <p className="text-sm text-muted-foreground">Bank-level encryption protects your funds</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Confirm Dialog */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Withdrawal</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p>Please confirm your withdrawal details:</p>
                  <div className="bg-muted p-4 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="font-medium">{currency?.symbol}{pendingAction?.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Bank:</span>
                      <span className="font-medium">{pendingAction?.bank}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Account:</span>
                      <span className="font-medium">{pendingAction?.account}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name:</span>
                      <span className="font-medium">{pendingAction?.account_name}</span>
                    </div>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={executeWithdrawal} className="bg-teal-600 hover:bg-teal-700">
                Confirm Withdrawal
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
